/**
 * Payrexx post-payment landing page (`/payment/payrexx/return`).
 *
 * Rendered when the backend bounce (`GET /api/purchase/payment/payrexx/return`)
 * redirects the browser back here. The URL `status` parameter is a display hint
 * only: the payment is always re-verified through
 * `POST /api/purchase/payment/verify`, then the attempt snapshot is reloaded so
 * the purchase wizard can be reopened with its full state.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import {
    parsePayrexxReturnParams,
    resumePayrexxReturn,
    verifyPaymentUntilSettled,
} from "../../components/TicketPurchase/payrexxReturn.tools";
import { TicketPurchaseLoader } from "../../components/TicketPurchase/TicketPurchaseLoader";

type ReturnPhase = "verifying" | "resuming" | "done" | "error";

/**
 * Payrexx web return landing page.
 *
 * @returns A spinner while the payment is re-checked, then a redirect to `/home`
 *   with the purchase modal reopened on the matching step.
 */
const PayrexxReturnPage: React.FC = () => {
    const location = useLocation();
    const [ phase, setPhase ] = useState<ReturnPhase>( "verifying" );
    const params = useMemo( () => parsePayrexxReturnParams( location.search ), [ location.search ] );
    const hasInvalidParams = params.attemptId === null;

    /** Guards the resume chain against React StrictMode double-invocation. */
    const resumeStartedRef = useRef( false );

    useEffect( () => {
        if ( resumeStartedRef.current || hasInvalidParams ) {
            return;
        }
        resumeStartedRef.current = true;

        const attemptId = params.attemptId ?? "";

        void ( async() => {
            const verifiedStatus = await verifyPaymentUntilSettled( attemptId, params.paymentId ?? undefined );

            setPhase( "resuming" );

            const resumed = await resumePayrexxReturn( attemptId, verifiedStatus, params.paymentId );

            setPhase( resumed ? "done" : "error" );
        } )();
    }, [ hasInvalidParams, params ] );

    if ( phase === "done" ) {
        // The purchase modal opens itself over /home thanks to the restored slice.
        return <Redirect to="/home" />;
    }

    return (
        <div className="flex h-full min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
            {phase === "error" || hasInvalidParams
? (
                <>
                    <h1 className="text-base font-bold">
                        Impossible de reprendre votre achat
                    </h1>
                    <p className="text-sm opacity-70">
                        La vérification du paiement a échoué. Si un montant a été débité, contactez le support FlowRide.
                    </p>
                    <a
                        className="text-sm font-semibold underline"
                        href="/home"
                    >
                        Retour à l&apos;accueil
                    </a>
                </>
            )
: (
                <>
                    <TicketPurchaseLoader />
                    <p className="text-sm opacity-70">
                        {phase === "verifying"
                            ? "Vérification du paiement…"
                            : "Reprise de votre commande…"}
                    </p>
                </>
            )}
        </div>
    );
};

export default PayrexxReturnPage;
